import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { decodeJWT } from '@circuitry-ai/doc-data';
import { sessionTimeoutManager } from '../utils/sessionTimeoutManager';

/**
 * Custom hook for managing session timeout
 * @param warningSeconds - Seconds before expiry to show warning (default: 60 for testing)
 * @returns Session timeout state and handlers
 */
export function useSessionTimeout(warningSeconds = 60) {
  const { getAccessTokenSilently, logout, isAuthenticated, isLoading } = useAuth0();
  const [showWarningModal, setShowWarningModal] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function setupTimers() {
      if (!isAuthenticated || isLoading) {
        sessionTimeoutManager.clear();
        setShowWarningModal(false);
        return;
      }

      try {
        // Get current token and exp
        const token = await getAccessTokenSilently();
        if (cancelled) return;

        const decoded = decodeJWT(token);
        if (!decoded?.exp) {
          console.warn('Session timeout: Could not decode token expiration');
          return;
        }

        const tokenExp = decoded.exp;

        sessionTimeoutManager.init({
          tokenExp,
          warningSeconds,
          onWarning: () => {
            if (!cancelled) setShowWarningModal(true);
          },
          onExpired: () => {
            if (!cancelled) {
              setShowWarningModal(false);
              logout({logoutParams: { returnTo: window.location.origin }});
            }
          },
        });
      } catch (error) {
        console.error('Session timeout: Error setting up timers', error);
      }
    }

    setupTimers();

    return () => {
      // Cleanup when component using this hook unmounts
      cancelled = true;
      sessionTimeoutManager.clear();
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, logout, warningSeconds]);

  /**
   * Handler when user clicks "Continue session"
   * Refreshes token and resets timers
   */
  const handleContinueSession = async () => {
    setShowWarningModal(false);

    try {
      // Get a fresh token (Auth0 will automatically refresh if needed)
      const newToken = await getAccessTokenSilently();

      const decoded = decodeJWT(newToken);
      if (!decoded?.exp) {
        console.warn('Session timeout: Could not decode new token expiration');
        return;
      }

      const newExp = decoded.exp;

      // Re-schedule timers with the new expiry
      sessionTimeoutManager.updateTokenExp(
        newExp,
        warningSeconds,
        () => setShowWarningModal(true),
        () => {
          setShowWarningModal(false);
          logout({logoutParams: { returnTo: window.location.origin }});
        }
      );
    } catch (error) {
      console.error('Session timeout: Error refreshing token', error);
      // If refresh fails, logout
      logout({logoutParams: { returnTo: window.location.origin }});
    }
  };

  /**
   * Handler when user clicks "End session"
   */
  const handleEndSession = () => {
    setShowWarningModal(false);
    sessionTimeoutManager.clear();
    logout({logoutParams: { returnTo: window.location.origin }});
  };

  return {
    showWarningModal,
    handleContinueSession,
    handleEndSession,
  };
}

